import { Button, Spinner } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';
import { BriefTemplateIntakeBody } from './BriefTemplateIntakeBody';
import {
  getBriefTemplateIntakeFooterActions,
  useBriefTemplateIntakeShell,
} from './useBriefTemplateIntakeShell';
import { TemplateGenerationStream } from '../TemplateGenerationStream/TemplateGenerationStream';

import type { BriefTemplateIntakeSheetProps } from './BriefTemplateIntakeSheet.types';

/**
 * Brief-template intake on a right-side sheet: the manual form, file upload,
 * the existing-artifacts picker, and generation streaming. The behaviour behind
 * all of it lives in `useBriefTemplateIntakeShell`.
 *
 * Parents MUST conditionally mount (`{open && <Sheet />}`) so the form state
 * resets on close.
 */
export const BriefTemplateIntakeSheet = ({
  open,
  onClose,
  onCreated,
}: BriefTemplateIntakeSheetProps) => {
  const { t } = useTranslation('briefs');
  const shell = useBriefTemplateIntakeShell({ onClose, onCreated });
  const { title, primary, secondary } = getBriefTemplateIntakeFooterActions(shell, t, onClose);

  const isLocked = primary.loading === true;

  return (
    <>
      {shell.generation.streamProps && (
        <TemplateGenerationStream {...shell.generation.streamProps} />
      )}
      <FormSidePanel
        open={open}
        onOpenChange={shell.handleOpenChange}
        title={title}
        className="gap-sm"
        footer={
          <div className="flex items-center gap-xs">
            <Button
              variant="outline"
              size="lg"
              onClick={secondary.onClick}
              disabled={(secondary.disabled ?? false) || isLocked}
            >
              {secondary.label}
            </Button>
            <Button
              size="lg"
              onClick={primary.onClick}
              disabled={(primary.disabled ?? false) || isLocked}
            >
              {isLocked && <Spinner />}
              {primary.label}
            </Button>
          </div>
        }
      >
        <BriefTemplateIntakeBody shell={shell} />
      </FormSidePanel>
    </>
  );
};

BriefTemplateIntakeSheet.displayName = 'BriefTemplateIntakeSheet';
